/**
 * Request bodies the Detection / Rules page sends for a tenant rule (Q03).
 *
 * The server is the truth (apps/api-controller/src/modules/detection):
 *   - POST /v1/detection/rules takes `CreateRuleSchema` (strict): name,
 *     description?, ruleType, severity, regoSource. `regoSource` holds a JSON
 *     rule body as text (`RuleBodySchema` in rule-engine.ts); Rego is refused.
 *   - POST /v1/detection/rules/dry-run takes `DryRunRuleSchema` (strict):
 *     ruleId or regoSource, plus `input` { finding?, event?, context? }, and
 *     answers with the rule engine's result (finalDecision, matchedRules).
 *
 * The page used to send Rego text, a dry-run of `{ ruleBody: { rego } }`, and
 * read a `wouldMatchCount` the server never returns. These builders are pure so
 * the exact bodies are pinned by rules-page-payload.test.tsx, and the server
 * side pins the same literals in detection/__tests__/rules-console-contract.test.ts.
 */
import type { CreateDetectionRuleInput, DetectionRuleTestInput } from '@/lib/api';

/** The decision kinds a rule body may name (`RuleBodySchema.kind`). */
export const RULE_BODY_KINDS = ['deny', 'alert', 'step_up', 'mask', 'break_glass', 'quarantine'] as const;
/** The predicate operators (`RuleOpSchema`). */
export const RULE_BODY_OPS = [
  'eq', 'neq', 'in', 'not_in', 'gt', 'gte', 'lt', 'lte', 'has', 'len_gt', 'len_lt', 'matches',
] as const;
/** `RuleTypeSchema`. */
export const RULE_TYPES = ['discovery_finding', 'activity_event', 'combined'] as const;
export type RuleType = (typeof RULE_TYPES)[number];
export type RuleSeverity = CreateDetectionRuleInput['severity'];

/** The body the New-rule editor starts from: a valid JSON rule body. */
export const DEFAULT_RULE_BODY_TEXT = JSON.stringify(
  {
    kind: 'alert',
    name: 'synthetic_bulk_read_of_identifiers',
    all: [
      { path: 'input.event.statementType', op: 'eq', value: 'SELECT' },
      { path: 'input.event.rowsReturned', op: 'gt', value: 10000 },
    ],
    recommendation: 'Review the bulk read with the data owner.',
  },
  null,
  2,
);

/** The input the dry-run tries the body against (matches the default body). */
export const DEFAULT_DRY_RUN_INPUT_TEXT = JSON.stringify(
  { event: { statementType: 'SELECT', rowsReturned: 250000 } },
  null,
  2,
);

export type Checked<T> = { ok: true; value: T } | { ok: false; error: string };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * A first check in the browser, so the common mistakes (Rego text, a missing
 * `kind`, a typo in an operator) are named before anything is sent. The
 * server's Zod schema still decides; this only mirrors its shape.
 */
export function checkRuleBodyText(text: string): Checked<string> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {
      ok: false,
      error: 'The rule body must be JSON (a rule body with "kind", "name" and "any" or "all"), not Rego.',
    };
  }
  if (!isPlainObject(parsed)) return { ok: false, error: 'The rule body must be a JSON object.' };
  const allowed = new Set(['kind', 'name', 'any', 'all', 'recommendation']);
  const extra = Object.keys(parsed).filter((k) => !allowed.has(k));
  if (extra.length > 0) return { ok: false, error: `Unknown field(s) in the rule body: ${extra.join(', ')}.` };
  if (!(RULE_BODY_KINDS as readonly unknown[]).includes(parsed['kind'])) {
    return { ok: false, error: `"kind" must be one of ${RULE_BODY_KINDS.join(', ')}.` };
  }
  if (typeof parsed['name'] !== 'string' || parsed['name'].length === 0) {
    return { ok: false, error: '"name" must be a non-empty string.' };
  }
  for (const group of ['any', 'all'] as const) {
    const preds = parsed[group];
    if (preds === undefined) continue;
    if (!Array.isArray(preds)) return { ok: false, error: `"${group}" must be a list of predicates.` };
    for (const [i, p] of preds.entries()) {
      if (!isPlainObject(p) || typeof p['path'] !== 'string' || p['path'].length === 0) {
        return { ok: false, error: `${group}[${i}] needs a "path" such as "input.event.rowsReturned".` };
      }
      if (!(RULE_BODY_OPS as readonly unknown[]).includes(p['op'])) {
        return { ok: false, error: `${group}[${i}].op must be one of ${RULE_BODY_OPS.join(', ')}.` };
      }
      const keys = Object.keys(p).filter((k) => k !== 'path' && k !== 'op' && k !== 'value');
      if (keys.length > 0) return { ok: false, error: `${group}[${i}] has unknown field(s): ${keys.join(', ')}.` };
    }
  }
  if (parsed['recommendation'] !== undefined && typeof parsed['recommendation'] !== 'string') {
    return { ok: false, error: '"recommendation" must be a string.' };
  }
  return { ok: true, value: text };
}

/** The test input: a JSON object holding only finding, event and context. */
export function checkDryRunInputText(text: string): Checked<DetectionRuleTestInput> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'The test input must be JSON.' };
  }
  if (!isPlainObject(parsed)) return { ok: false, error: 'The test input must be a JSON object.' };
  const out: DetectionRuleTestInput = {};
  for (const [k, v] of Object.entries(parsed)) {
    if (k !== 'finding' && k !== 'event' && k !== 'context') {
      return { ok: false, error: `The test input takes "finding", "event" and "context"; "${k}" is not one of them.` };
    }
    if (!isPlainObject(v)) return { ok: false, error: `"${k}" must be a JSON object.` };
    out[k] = v;
  }
  return { ok: true, value: out };
}

export interface NewRuleForm {
  name: string;
  description: string;
  ruleType: RuleType;
  severity: RuleSeverity;
  bodyText: string;
}

/** POST /v1/detection/rules. The body text is sent as typed, once it checks. */
export function buildCreateRuleRequest(form: NewRuleForm): Checked<CreateDetectionRuleInput> {
  const name = form.name.trim();
  if (name.length === 0) return { ok: false, error: 'Give the rule a name.' };
  if (name.length > 255) return { ok: false, error: 'The name is longer than 255 characters.' };
  const body = checkRuleBodyText(form.bodyText);
  if (!body.ok) return body;
  const description = form.description.trim();
  return {
    ok: true,
    value: {
      name,
      ...(description.length > 0 ? { description } : {}),
      ruleType: form.ruleType,
      severity: form.severity,
      regoSource: body.value,
    },
  };
}

/** POST /v1/detection/rules/dry-run for an unsaved body. */
export function buildDryRunRequest(
  bodyText: string,
  inputText: string,
): Checked<{ regoSource: string; input: DetectionRuleTestInput }> {
  const body = checkRuleBodyText(bodyText);
  if (!body.ok) return body;
  const input = checkDryRunInputText(inputText);
  if (!input.ok) return input;
  return { ok: true, value: { regoSource: body.value, input: input.value } };
}

/**
 * The server's refusal in words: a Zod 400 carries `errors` [{ path, message }]
 * on the problem body, which the plain message ("Validation failed - Request
 * did not match expected schema.") leaves out.
 */
export function describeApiError(e: unknown, fallback: string): string {
  if (!(e instanceof Error)) return fallback;
  const body = (e as { body?: unknown }).body;
  const errors = isPlainObject(body) && Array.isArray(body['errors']) ? body['errors'] : [];
  const parts = errors
    .filter(isPlainObject)
    .map((x) => `${typeof x['path'] === 'string' && x['path'] ? `${x['path']}: ` : ''}${String(x['message'] ?? '')}`)
    .filter((t) => t.length > 0);
  return parts.length > 0 ? `${e.message} (${parts.join('; ')})` : e.message || fallback;
}

/**
 * Who approved and who published a rule (Q04). Moving a rule to dry run is
 * its first approval (`approvedByUserId`, `approvedAt`); the server records
 * the publisher and the tenant's second-approver policy in
 * `metadata.publication` when it publishes.
 */
export interface RuleApproval {
  approvedByUserId: string | null;
  approvedAt: string | null;
  publishedByUserId: string | null;
  secondApprover: string | null;
}

export function ruleApproval(rule: {
  approvedByUserId: string | null;
  approvedAt: string | null;
  metadata: Record<string, unknown> | null | undefined;
}): RuleApproval {
  const pub = isPlainObject(rule.metadata) ? rule.metadata['publication'] : undefined;
  const p = isPlainObject(pub) ? pub : {};
  return {
    approvedByUserId: rule.approvedByUserId,
    approvedAt: rule.approvedAt,
    publishedByUserId: typeof p['publishedByUserId'] === 'string' ? p['publishedByUserId'] : null,
    secondApprover: typeof p['secondApprover'] === 'string' ? p['secondApprover'] : null,
  };
}

/** The first 8 characters of a user id, for a row; the drawer shows it whole. */
export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : '';
}

/** The `kind` of a stored JSON rule body, or null when it is not one. */
export function ruleBodyKind(regoSource: string | null | undefined): string | null {
  try {
    const k = (JSON.parse(regoSource ?? '') as { kind?: unknown } | null)?.kind;
    return typeof k === 'string' ? k : null;
  } catch {
    return null;
  }
}

/**
 * Q06 round 4, round 5: what a /decide publish warning means for the gateway,
 * in plain words, by the rule's kind. A "matches every statement" warning (a
 * neq or not_in on a field the gateway's /decide never carries) means the
 * predicate is always true there, so the rule's outcome applies to every
 * statement its other conditions allow (with no other conditions, every
 * statement of the tenant): only a deny or quarantine rule refuses; step_up
 * and break_glass step up; mask masks, or refuses what the gateway cannot
 * mask; alert never refuses but records each one. Pausing also takes the rule
 * off /v1/detection/evaluate. Null for other warnings.
 */
export function decideWarningConsequence(warning: string, kind?: string | null): string | null {
  if (!warning.includes('matches every statement')) return null;
  const scope = 'every statement on the gateway that its other conditions allow (with no other conditions, every statement of this tenant)';
  let effect: string;
  switch (kind) {
    case 'deny':
    case 'quarantine':
      effect = `With ddr.tenant_rules_on_decide at enforce, this rule will refuse ${scope}.`;
      break;
    case 'step_up':
    case 'break_glass':
      effect = `With ddr.tenant_rules_on_decide at enforce, this rule will require a step-up for ${scope}.`;
      break;
    case 'mask':
      effect = `With ddr.tenant_rules_on_decide at enforce, this rule will ask to mask ${scope}; a statement the gateway cannot mask is refused instead.`;
      break;
    case 'alert':
      effect = `With ddr.tenant_rules_on_decide at monitor or enforce, this rule will not refuse anything, but it will record an alert, a decision row and a SIEM event for ${scope}.`;
      break;
    default:
      effect = `With ddr.tenant_rules_on_decide at enforce, this rule's outcome will apply to ${scope}.`;
  }
  return `${effect} Pause it now unless that is intended; pausing also takes it off /v1/detection/evaluate.`;
}
